import { reschedulableProcedureStatuses, ProcedureStatus, DraftWaypointStatus } from '@caresend/types';
import { deduplicateArray, getBrickAndMortarLab, nullishFilter, objectFilter, hasStatus, getOfficePath, errorSuffix } from '@caresend/utils';
import { defineStore } from 'pinia';
import { computed, ref, reactive, watch, readonly } from 'vue';
import 'firebase/auth';
import { cancelVisitRequest } from '../../../database/firebaseConfig/cloudFunctions.js';
import '../../../database/firebaseConfig/init.js';
import { getValueOnce } from '../../../database/firebaseConfig/methods.js';
import '@sentry/vue';
import { toastErrorAndReport, toastSuccess } from '../../../functions/toast/toast.js';
import '@fortawesome/fontawesome-svg-core';
import 'buefy';
import '../../../components/ui/Icon.vue.js';
import '../../../functions/configuration/icons.js';
import '../../../database/algoliaConfig/indices.js';
import 'mobile-device-detect';
import '../../../functions/tracking/index.js';
import '../../../router/helpers/initRouter.js';
import { getStore } from '../../helpers/initStore.js';
import '@firebase/auth';
import 'browser-image-compression';
import 'firebase/storage';
import 'js-sha256';
import { isPatientUser } from '../../helpers.js';
import { getLocalDenormalizedDraftWaypointForScheduling } from '../../modules/schedulingFlow/helpers/local.js';
import { getAllSampleShipmentInfo, getReschedulingAllowedBasedOnProcedureStatus, getReschedulingAllowedDueToSupplySideProcedureCancelation, getPatientAndTestNames, getPatientIDsOnProcedureInfos, getPatientsAndTestsByTrackingURL, ReschedulingAllowedReason, getRecentLocallyStoredDraftWaypointIDs, getDraftWaypointsFromDenormalizedOrders, decryptDenormalizedDraftWaypoints, getDraftWaypointsFilteredByCurrentUser, getUpdatedAlgoliaOrdersAPIKey, getOrdersForDraftWaypointsFromAlgolia, fetchSupplyInstructionsForDraftWaypoint, isCancelVisitDryRun } from './helpers.js';
import { getLocalDenormalizedDraftWaypointForSampleTrackingNumberUpdate, getLocalDenormalizedDraftWaypointForCancelation } from './local.js';

/* eslint sort-keys-fix/sort-keys-fix: "error" */
/**
 * The data stored here is used for visits, namely on the patient app because
 * patients do not have the necessary permissions to fetch all required data
 * from Firebase. This makes it easier to fetch denormalized data from
 * Algolia.
 *
 * In the future, it would be best to create a new index in Algolia that
 * contains the denormalized draft waypoints and fetch from there rather than
 * using the denormalized orders index to fetch the draft waypoints inside.
 */
const useDraftWaypointsStore = defineStore('draftWaypoints', () => {
    const store = getStore();
    const isPatient = computed(isPatientUser);
    const algoliaKey = ref(null);
    const denormalizedDraftWaypoints = {};
    const locallyStoredDraftWaypointIDs = {};
    const patientVisitListIDs = [];
    const staffingOffices = {};
    const supplyInstructions = {};
    const state = reactive({
        algoliaKey,
        denormalizedDraftWaypoints,
        /**
         * This is used to keep track of which draft waypoints are being sourced locally
         * rather than from Algolia which is the source of truth. We do this because it
         * takes time for the denormalized order to be updated and want to show the user
         * an updated UI on the patient app sooner
         *
         * By draftWaypointID to timestamp of when locally updated
         */
        locallyStoredDraftWaypointIDs,
        /**
         * The source of truth for the draft waypoints to display on the patient
         * visit list.
         */
        patientVisitListIDs,
        staffingOffices,
        /**
         * Supply instructions by procedure ID.
         *
         * The options must be present in order for a user to enter the scheduling
         * flow. However, if a user enters the scheduling flow directly, the draft
         * waypoint is not present and will be fetched during the scheduling flow
         * initialization.
         */
        supplyInstructions,
    });
    const isLoading = ref(true);
    const getters = {
        getAllSampleShipmentInfo: (draftWaypointID) => {
            const patientProcedureData = getters.getPatientProcedureData(draftWaypointID);
            if (!patientProcedureData)
                return [];
            return getAllSampleShipmentInfo(patientProcedureData);
        },
        getAreAllSupplyInstructionsPresentForDraftWaypoint: (draftWaypointID, isRescheduling) => {
            const proceduresToCheck = getters.getProcedureIDsOnDraftWaypoint(draftWaypointID, isRescheduling);
            const isSomeInstructionMissing = proceduresToCheck.some((procedureID) => !state.supplyInstructions[procedureID]);
            return !isSomeInstructionMissing;
        },
        getBookingIDsOnDraftWaypoint: (draftWaypointID) => {
            const patientProcedureData = getters.getPatientProcedureData(draftWaypointID);
            if (!patientProcedureData)
                return [];
            const bookingIDs = Object.values(patientProcedureData.procedures).map((procedureInfo) => procedureInfo.bookingID);
            return deduplicateArray(bookingIDs);
        },
        getBookingsMissingSampleShipmentTrackingNumbers: (draftWaypointID) => {
            const shipmentTracking = getters.getShipmentTracking(draftWaypointID);
            if (!shipmentTracking)
                return [];
            const bookingIDs = getters.getBookingIDsOnDraftWaypoint(draftWaypointID);
            return bookingIDs.filter((bookingID) => { var _a, _b; return !((_b = (_a = shipmentTracking.sampleTrackingURLs) === null || _a === void 0 ? void 0 : _a[bookingID]) === null || _b === void 0 ? void 0 : _b.length); });
        },
        getBrickAndMortarLabInfo: (draftWaypointID) => {
            var _a;
            const facilityDetails = getters.getFacilityDetails(draftWaypointID);
            if (!facilityDetails)
                return undefined;
            const { name, phoneNumber } = facilityDetails;
            const info = getBrickAndMortarLab(facilityDetails, name !== null && name !== void 0 ? name : 'Unknown location', phoneNumber, (_a = getters.getDenormalizedDraftWaypoint(draftWaypointID)) === null || _a === void 0 ? void 0 : _a.location);
            return info;
        },
        getCancelationReasonsOnDraftWaypoint: (draftWaypointID) => {
            const procedureInfos = getters.getProcedureInfoOnDraftWaypoint(draftWaypointID);
            return procedureInfos.map((procedureInfo) => { var _a; return (_a = procedureInfo.canceled) === null || _a === void 0 ? void 0 : _a.reason; }).filter(nullishFilter);
        },
        getDenormalizedDraftWaypoint: (draftWaypointID) => state.denormalizedDraftWaypoints[draftWaypointID],
        getDenormalizedDraftWaypointsForPatientVisitList: () => state.patientVisitListIDs.map((id) => state.denormalizedDraftWaypoints[id]).filter(nullishFilter),
        getDraftWaypointAccessInstructions: (draftWaypointID) => {
            var _a, _b, _c;
            return (_c = (_b = (_a = state.denormalizedDraftWaypoints[draftWaypointID]) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.additionalInstructions) !== null && _c !== void 0 ? _c : null;
        },
        getDraftWaypointAddress: (draftWaypointID) => {
            var _a, _b, _c;
            return (_c = (_b = (_a = state.denormalizedDraftWaypoints[draftWaypointID]) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.formattedAddress) !== null && _c !== void 0 ? _c : null;
        },
        getDraftWaypointApartmentNumber: (draftWaypointID) => {
            var _a, _b, _c;
            return (_c = (_b = (_a = state.denormalizedDraftWaypoints[draftWaypointID]) === null || _a === void 0 ? void 0 : _a.location) === null || _b === void 0 ? void 0 : _b.aptNumber) !== null && _c !== void 0 ? _c : null;
        },
        getDraftWaypointDispatchSteps: (draftWaypointID) => { var _a, _b; return (_b = (_a = state.denormalizedDraftWaypoints[draftWaypointID]) === null || _a === void 0 ? void 0 : _a.dispatchSteps) !== null && _b !== void 0 ? _b : []; },
        getFacilityDetails: (draftWaypointID) => { var _a; return (_a = state.denormalizedDraftWaypoints[draftWaypointID]) === null || _a === void 0 ? void 0 : _a.facilityDetails; },
        getFacilityDetailsOnDraftWaypoint: (draftWaypointID) => { var _a; return (_a = state.denormalizedDraftWaypoints[draftWaypointID]) === null || _a === void 0 ? void 0 : _a.facilityDetails; },
        getIsCurrentUserSchedulingUser: (draftWaypointID) => {
            var _a;
            const schedulingUserID = getters.getSchedulingUserID(draftWaypointID);
            const currentUserID = (_a = store.state.auth.user) === null || _a === void 0 ? void 0 : _a.id;
            return !!schedulingUserID && schedulingUserID === currentUserID;
        },
        /** We are not checking the lead time for rescheduling as of 2/15/25 */
        getIsReschedulingPossible: (draftWaypointID) => (getReschedulingAllowedBasedOnProcedureStatus(draftWaypointID, getters)
            || getReschedulingAllowedDueToSupplySideProcedureCancelation(draftWaypointID, getters)),
        getLabOrderIDsProcedureID: (draftWaypointID) => {
            const denormalizedDraftWaypoint = getters.getDenormalizedDraftWaypoint(draftWaypointID);
            if (!denormalizedDraftWaypoint)
                return undefined;
            return denormalizedDraftWaypoint.labOrderIDs;
        },
        getLocallyStoredDraftWaypoints: () => objectFilter(state.denormalizedDraftWaypoints, ({ id }) => !!state.locallyStoredDraftWaypointIDs[id]),
        getNumberOfPatientsOnVisit: (draftWaypointID) => {
            var _a;
            const patientProcedureData = getters.getPatientProcedureData(draftWaypointID);
            if (!patientProcedureData)
                return 0;
            const procedureInfos = (_a = patientProcedureData.procedures) !== null && _a !== void 0 ? _a : {};
            const uniquePatientIDs = new Set(Object.values(procedureInfos).map((procedureInfo) => procedureInfo.patientID));
            return uniquePatientIDs.size;
        },
        getNurseName: (draftWaypointID) => {
            var _a, _b;
            const patientProcedureData = getters.getPatientProcedureData(draftWaypointID);
            if (!patientProcedureData)
                return null;
            const allProcedureInfos = Object.values(patientProcedureData.procedures);
            if (allProcedureInfos.length === 0)
                return null;
            return (_b = (_a = allProcedureInfos[0]) === null || _a === void 0 ? void 0 : _a.nurseName) !== null && _b !== void 0 ? _b : null;
        },
        getNurseTitleFromDispatchSteps: (draftWaypointID) => {
            var _a;
            const dispatchSteps = getters.getDraftWaypointDispatchSteps(draftWaypointID);
            if (!dispatchSteps)
                return null;
            const secondStep = dispatchSteps[1];
            // The second step will contain the nurse's medical title. If this is a
            // facility visit (which doesn't contain a nurse), the second step will
            // be a waypoint step
            if (!secondStep || secondStep.waypointID)
                return null;
            const secondStepTitle = secondStep.title;
            if (!secondStepTitle)
                return null;
            return (_a = secondStepTitle.split(' ')[0]) !== null && _a !== void 0 ? _a : null;
        },
        getPatientAndTestNames: (draftWaypointID) => {
            var _a;
            const patientProcedureData = getters.getPatientProcedureData(draftWaypointID);
            if (!patientProcedureData)
                return [];
            return getPatientAndTestNames(patientProcedureData, (_a = store.state.variables.variables) === null || _a === void 0 ? void 0 : _a.tasks);
        },
        getPatientIDsOnDraftWaypoint: (draftWaypointID) => {
            const patientProcedureData = getters.getPatientProcedureData(draftWaypointID);
            if (!patientProcedureData)
                return [];
            return getPatientIDsOnProcedureInfos(patientProcedureData.procedures);
        },
        getPatientNameByID: (draftWaypointID, patientID) => {
            var _a, _b;
            const patientProcedureData = getters.getPatientProcedureData(draftWaypointID);
            if (!patientProcedureData)
                return null;
            return (_b = (_a = Object.values(patientProcedureData.procedures).find((procedureInfo) => procedureInfo.patientID === patientID)) === null || _a === void 0 ? void 0 : _a.patientName) !== null && _b !== void 0 ? _b : null;
        },
        getPatientProcedureData: (draftWaypointID) => { var _a; return (_a = state.denormalizedDraftWaypoints[draftWaypointID]) === null || _a === void 0 ? void 0 : _a.patientProcedureData; },
        getPatientShipmentTrackingURLsByBookingID: (draftWaypointID) => {
            const shipmentTracking = getters.getShipmentTracking(draftWaypointID);
            if (!shipmentTracking)
                return undefined;
            return shipmentTracking === null || shipmentTracking === void 0 ? void 0 : shipmentTracking.patientTrackingURLs;
        },
        getPatientVisitListIDs: () => state.patientVisitListIDs,
        getPatientWaypointInfo: (draftWaypointID) => { var _a; return (_a = state.denormalizedDraftWaypoints[draftWaypointID]) === null || _a === void 0 ? void 0 : _a.patientWaypointInfo; },
        getPatientsAndTestsByTrackingURL: (draftWaypointID, type) => {
            const patientProcedureData = getters.getPatientProcedureData(draftWaypointID);
            if (!patientProcedureData)
                return undefined;
            return getPatientsAndTestsByTrackingURL(patientProcedureData, type);
        },
        getPrescriberNameOnDraftWaypoint: (draftWaypointID) => {
            var _a, _b, _c;
            return (_c = (_b = (_a = state.denormalizedDraftWaypoints[draftWaypointID]) === null || _a === void 0 ? void 0 : _a.prescriber) === null || _b === void 0 ? void 0 : _b.formattedName) !== null && _c !== void 0 ? _c : 'Unknown prescriber';
        },
        getPrimaryContactName: (draftWaypointID) => {
            var _a, _b;
            return (_b = (_a = state.denormalizedDraftWaypoints[draftWaypointID]) === null || _a === void 0 ? void 0 : _a.primaryContactName) !== null && _b !== void 0 ? _b : null;
        },
        /**
         * Primarily used to filter ineligible procedures for supply side procedure
         * cancelation rescheduling.
         */
        getProcedureIDsIneligibleForRescheduling: (draftWaypointID) => {
            const patientProcedureData = getters.getPatientProcedureData(draftWaypointID);
            if (!patientProcedureData)
                return [];
            const rescheduleType = getters.getReschedulingAllowedReason(draftWaypointID);
            if (!rescheduleType)
                return [];
            return Object.values(patientProcedureData.procedures).filter((procedureInfo) => (hasStatus(procedureInfo.status, reschedulableProcedureStatuses)
                && procedureInfo.disallowRescheduling)
                || hasStatus(procedureInfo.status, ProcedureStatus.COMPLETED)).map((procedureInfo) => procedureInfo.id);
        },
        /**
         * Gets the procedureIDs on the draft waypoint. If rescheduling due to supply
         * side procedure cancelation, filters out ineligible procedures.
         */
        getProcedureIDsOnDraftWaypoint: (draftWaypointID, isRescheduling = false) => {
            const denormalizedDraftWaypoint = getters.getDenormalizedDraftWaypoint(draftWaypointID);
            if (!denormalizedDraftWaypoint)
                return [];
            if (!isRescheduling)
                return denormalizedDraftWaypoint.procedureIDs;
            const proceduresIneligibleForRescheduling = getters.getProcedureIDsIneligibleForRescheduling(draftWaypointID);
            return denormalizedDraftWaypoint.procedureIDs.filter((id) => !proceduresIneligibleForRescheduling.includes(id));
        },
        getProcedureInfoOnDraftWaypoint: (draftWaypointID) => {
            const patientProcedureData = getters.getPatientProcedureData(draftWaypointID);
            if (!patientProcedureData)
                return [];
            return Object.values(patientProcedureData.procedures);
        },
        getReschedulingAllowedReason: (draftWaypointID) => {
            const reschedulingAllowed = getters.getIsReschedulingPossible(draftWaypointID);
            if (!reschedulingAllowed)
                return undefined;
            const isAllowedDueToSupplySideProcedureCancelation = getReschedulingAllowedDueToSupplySideProcedureCancelation(draftWaypointID, getters);
            return isAllowedDueToSupplySideProcedureCancelation
                ? ReschedulingAllowedReason.SUPPLY_SIDE_PROCEDURE_CANCELED
                : ReschedulingAllowedReason.PROCEDURE_STATUS;
        },
        getSampleTrackingURLsByBookingID: (draftWaypointID) => {
            const shipmentTracking = getters.getShipmentTracking(draftWaypointID);
            return shipmentTracking === null || shipmentTracking === void 0 ? void 0 : shipmentTracking.sampleTrackingURLs;
        },
        getSchedulingUserID: (draftWaypointID) => {
            var _a, _b;
            return (_b = (_a = state.denormalizedDraftWaypoints[draftWaypointID]) === null || _a === void 0 ? void 0 : _a.schedulingUserID) !== null && _b !== void 0 ? _b : null;
        },
        getSchedulingUserName: (draftWaypointID) => {
            var _a, _b;
            return (_b = (_a = state.denormalizedDraftWaypoints[draftWaypointID]) === null || _a === void 0 ? void 0 : _a.schedulingUserName) !== null && _b !== void 0 ? _b : null;
        },
        getShipmentTracking: (draftWaypointID) => {
            const patientProcedureData = getters.getPatientProcedureData(draftWaypointID);
            if (!patientProcedureData)
                return undefined;
            return patientProcedureData.shipmentTracking;
        },
        getStaffingOffice: (draftWaypointID) => {
            var _a;
            const { staffingOfficeID } = (_a = getters.getDenormalizedDraftWaypoint(draftWaypointID)) !== null && _a !== void 0 ? _a : {};
            if (!staffingOfficeID)
                return undefined;
            return state.staffingOffices[staffingOfficeID];
        },
        getStaffingOfficeLeadTime: (draftWaypointID) => {
            var _a;
            const office = getters.getStaffingOffice(draftWaypointID);
            if (!office)
                return 24;
            return (_a = office.rescheduleLeadTime) !== null && _a !== void 0 ? _a : 24;
        },
        getStaffingOfficeName: (draftWaypointID) => { var _a, _b; return (_b = (_a = getters.getStaffingOffice(draftWaypointID)) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.name; },
        getStaffingOfficePhone: (draftWaypointID) => { var _a, _b; return (_b = (_a = getters.getStaffingOffice(draftWaypointID)) === null || _a === void 0 ? void 0 : _a.info) === null || _b === void 0 ? void 0 : _b.phone; },
        getSupplyConditionsOnDraftWaypoint: (draftWaypointID) => {
            const procedureInfos = getters.getProcedureInfoOnDraftWaypoint(draftWaypointID);
            return procedureInfos.map((procedureInfo) => { var _a; return (_a = procedureInfo.canceled) === null || _a === void 0 ? void 0 : _a.supplyCondition; }).filter(nullishFilter);
        },
        getSupplyInstructionsOnDraftWaypoint: (draftWaypointID, isRescheduling) => {
            const procedureIDs = getters.getProcedureIDsOnDraftWaypoint(draftWaypointID, isRescheduling);
            const supplyInstructionConfigOptions = procedureIDs.reduce((options, procedureID) => {
                const supplyInstruction = getters.getSupplyInstructionsOnProcedure(procedureID);
                if (supplyInstruction) {
                    return {
                        ...options,
                        [procedureID]: supplyInstruction,
                    };
                }
                return options;
            }, {});
            return supplyInstructionConfigOptions;
        },
        getSupplyInstructionsOnProcedure: (procedureID) => state.supplyInstructions[procedureID],
    };
    /**
     * Clears the algolia key, draft waypoints, and the patient visit list IDs
     * when the user changes.
     */
    watch(() => { var _a; return (_a = store.state.auth.user) === null || _a === void 0 ? void 0 : _a.id; }, (userID, previousUserID) => {
        if (userID && userID === previousUserID)
            return;
        state.algoliaKey = null;
        state.denormalizedDraftWaypoints = {};
        state.patientVisitListIDs = [];
    }, { immediate: true });
    const fetchStaffingOffice = async (draftWaypointID) => {
        const draftWaypoint = getters.getDenormalizedDraftWaypoint(draftWaypointID);
        const { staffingOfficeID } = draftWaypoint !== null && draftWaypoint !== void 0 ? draftWaypoint : {};
        if (!staffingOfficeID)
            return;
        const office = await getValueOnce(getOfficePath(staffingOfficeID));
        if (!office)
            return;
        state.staffingOffices[staffingOfficeID] = office;
    };
    /**
     * Sorts draft waypoints for display on the patient visit list. In the future,
     * it would be better to move sorting logic to a replica index in Algolia.
     */
    const getSortedPatientVisitIDs = (draftWaypoints) => {
        const sorted = Object.values(draftWaypoints).sort((a, b) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            if (hasStatus(a, DraftWaypointStatus.PENDING)
                && !hasStatus(b, DraftWaypointStatus.PENDING))
                return -1;
            if (!hasStatus(a, DraftWaypointStatus.PENDING)
                && hasStatus(b, DraftWaypointStatus.PENDING))
                return 1;
            const aDate = (_d = (_b = (_a = a.date) === null || _a === void 0 ? void 0 : _a.timestamp) !== null && _b !== void 0 ? _b : (_c = a.date) === null || _c === void 0 ? void 0 : _c.timestamp) !== null && _d !== void 0 ? _d : 0;
            const bDate = (_h = (_f = (_e = b.date) === null || _e === void 0 ? void 0 : _e.timestamp) !== null && _f !== void 0 ? _f : (_g = b.date) === null || _g === void 0 ? void 0 : _g.timestamp) !== null && _h !== void 0 ? _h : 0;
            return new Date(aDate).getTime() - new Date(bDate).getTime();
        });
        return sorted.map((draftWaypoint) => draftWaypoint.id);
    };
    /**
     * Given fetched denormalized orders, store a group of decrypted
     * denormalized draft waypoints. Recently locally stored draft
     * waypoints from the scheduling flow take precedence over those fetched from
     * Algolia.
     *
     * - Patient visit list - Used by the `fetchDenormalizedDraftWaypoints`
     *   function.
     * - Pro visit list - Used by the `orders` pinia module after fetching.
     */
    const storeDecryptedDenormalizedDraftWaypoints = async (denormalizedOrders, isList = false) => {
        state.locallyStoredDraftWaypointIDs
            = getRecentLocallyStoredDraftWaypointIDs(state.locallyStoredDraftWaypointIDs);
        const locallyStoredDraftWaypoints = getters.getLocallyStoredDraftWaypoints();
        try {
            const draftWaypoints = getDraftWaypointsFromDenormalizedOrders(denormalizedOrders);
            const decryptedDraftWaypoints = await decryptDenormalizedDraftWaypoints(draftWaypoints);
            // TODO: If denormalized draft waypoint algolia index created, move this
            // filter to the algolia query.
            const algoliaDraftWaypointsToStore = isPatientUser()
                ? getDraftWaypointsFilteredByCurrentUser(decryptedDraftWaypoints)
                : decryptedDraftWaypoints;
            const draftWaypointsToStore = {
                ...algoliaDraftWaypointsToStore,
                // Locally stored draft waypoints are spread last so that they overwrite
                // any draft waypoints that came from Algolia.
                ...locallyStoredDraftWaypoints,
            };
            state.denormalizedDraftWaypoints = {
                ...state.denormalizedDraftWaypoints,
                ...draftWaypointsToStore,
            };
            if (isList) {
                state.patientVisitListIDs = getSortedPatientVisitIDs(draftWaypointsToStore);
            }
        }
        catch (error) {
            toastErrorAndReport(`There was a problem loading your orders. ${errorSuffix}`, error);
        }
    };
    /**
     * Fetches, decrypts, and stores denormalized draft waypoint(s)
     * - Waypoint ID passed (visit page)
     *   - Fetches a single draft waypoint
     *   - Fetches the staffing office for the draft waypoint
     * - No waypoint ID passed (visit list)
     *   - If patient user, fetches a list of draft waypoints for the current user
     *   - If an office user, fetches a list of draft waypoints for the current
     *     user's office (Currently unused. Instead, the pinia orders store
     *     handles pro fetching and calls `storeDecryptedDenormalizedDraftWaypoints`.)
     *   - Stores a sorted list of draft waypoint IDs for display on the patient
     *     visit list
     */
    const fetchDenormalizedDraftWaypoints = async (draftWaypointID) => {
        var _a, _b;
        isLoading.value = true;
        const isListFetch = !draftWaypointID;
        if (isListFetch)
            state.patientVisitListIDs = [];
        try {
            state.algoliaKey = await getUpdatedAlgoliaOrdersAPIKey(state.algoliaKey, isPatient.value, isPatient.value ? undefined : (_a = store.state.office.office) === null || _a === void 0 ? void 0 : _a.id);
            if (!((_b = state.algoliaKey) === null || _b === void 0 ? void 0 : _b.denormalizedOrdersKey))
                throw Error('Missing key');
            /** Get all draft waypoints from algolia */
            const denormalizedOrders = await getOrdersForDraftWaypointsFromAlgolia(state.algoliaKey.denormalizedOrdersKey, draftWaypointID);
            await Promise.all([
                storeDecryptedDenormalizedDraftWaypoints(denormalizedOrders, isListFetch),
                !isListFetch
                    ? fetchStaffingOffice(draftWaypointID)
                    : undefined,
            ]);
        }
        catch (error) {
            toastErrorAndReport(`There was a problem loading your orders. ${errorSuffix}`, error);
        }
        finally {
            isLoading.value = false;
        }
    };
    /**
     * Stores the denormalized draft waypoint locally.
     */
    const setDraftWaypointLocally = (denormalizedDraftWaypoint) => {
        const { id } = denormalizedDraftWaypoint;
        state.locallyStoredDraftWaypointIDs[id] = Date.now();
        state.denormalizedDraftWaypoints[id] = denormalizedDraftWaypoint;
    };
    /**
     * Stores the apartment number and access instructions for a draft waypoint
     * locally if a patient updates them.
     */
    const setDraftWaypointApartmentAndInstructionsLocally = (draftWaypointID, updatedLocation) => {
        const denormalizedDraftWaypoint = getters.getDenormalizedDraftWaypoint(draftWaypointID);
        if (!denormalizedDraftWaypoint)
            throw Error('Missing draft waypoint');
        const updatedDraftWaypoint = {
            ...denormalizedDraftWaypoint,
            location: updatedLocation,
        };
        setDraftWaypointLocally(updatedDraftWaypoint);
    };
    /**
     * Stores the updated shipment tracking numbers for a draft waypoint locally
     * if a user updates them through the EditSampleTrackingNumbersAlert.
     */
    const setDraftWaypointShipmentTrackingLocally = (draftWaypointID, updatedShipmentInfos) => {
        const denormalizedDraftWaypoint = getters.getDenormalizedDraftWaypoint(draftWaypointID);
        if (!denormalizedDraftWaypoint)
            throw Error('Missing draft waypoint');
        const updatedDraftWaypoint = getLocalDenormalizedDraftWaypointForSampleTrackingNumberUpdate(updatedShipmentInfos, denormalizedDraftWaypoint);
        setDraftWaypointLocally(updatedDraftWaypoint);
    };
    /**
     * For rescheduling or cancelation, temporarily stores the canceled draft
     * waypoint locally while algolia updates. If no draft waypoint is
     * passed, we are rescheduling and the old draft waypoint from the
     * scheduling flow module is used. The draft waypoint is then stored
     * locally with a canceled status.
     */
    const setDraftWaypointCanceledLocally = (draftWaypointID, canceled, proceduresToNotCancel) => {
        const isCancelation = !!draftWaypointID && !!canceled;
        let denormalizedDraftWaypoint;
        if (isCancelation) {
            const oldDraftWaypointData = getters.getDenormalizedDraftWaypoint(draftWaypointID);
            if (!oldDraftWaypointData)
                throw Error('Missing old draft waypoint');
            denormalizedDraftWaypoint = getLocalDenormalizedDraftWaypointForCancelation(canceled, oldDraftWaypointData, proceduresToNotCancel);
        }
        else {
            const { oldDraftWaypoint } = store.state.schedulingFlow.localState;
            if (!oldDraftWaypoint)
                throw Error('Missing old draft waypoint');
            denormalizedDraftWaypoint = getLocalDenormalizedDraftWaypointForScheduling(oldDraftWaypoint);
        }
        setDraftWaypointLocally(denormalizedDraftWaypoint);
    };
    /**
     * Stores the just-scheduled draft waypoint for display of the visit page
     * and patient visit list temporarily while algolia updates. The draft
     * waypoint in the scheduling flow module must be updated to reflect the
     * scheduled state prior to calling.
     */
    const setDraftWaypointScheduledLocally = () => {
        const { draftWaypoint } = store.state.schedulingFlow;
        if (!draftWaypoint)
            throw Error('Missing draft waypoint');
        const denormalizedDraftWaypoint = getLocalDenormalizedDraftWaypointForScheduling(draftWaypoint);
        setDraftWaypointLocally(denormalizedDraftWaypoint);
    };
    /**
     * Gets the supply instructions for all of the procedures on a draft waypoint.
     * If the instructions are not found in the store, they are fetched from the
     * database. If any of the procedures are missing instructions, the function
     * returns undefined.
     */
    const getSupplyInstructions = async (draftWaypointID, isRescheduling) => {
        const denormalizedDraftWaypoint = getters.getDenormalizedDraftWaypoint(draftWaypointID);
        if (!denormalizedDraftWaypoint)
            await fetchDenormalizedDraftWaypoints(draftWaypointID);
        const allInstructionsPresent = getters
            .getAreAllSupplyInstructionsPresentForDraftWaypoint(draftWaypointID, isRescheduling);
        if (!allInstructionsPresent) {
            state.supplyInstructions = await fetchSupplyInstructionsForDraftWaypoint(draftWaypointID, getters, isRescheduling);
            const allInstructionsPresentSecondCheck = getters
                .getAreAllSupplyInstructionsPresentForDraftWaypoint(draftWaypointID, isRescheduling);
            if (!allInstructionsPresentSecondCheck) {
                return undefined;
            }
        }
        return getters.getSupplyInstructionsOnDraftWaypoint(draftWaypointID, isRescheduling);
    };
    const cancelVisit = async (draftWaypointID, cancelationReason) => {
        const draftWaypoint = store.state.waypoint.draftWaypoints[draftWaypointID];
        const isDryRun = isCancelVisitDryRun(draftWaypoint);
        const cancelVisitResponse = await cancelVisitRequest({
            cancelationReason,
            draftWaypointID,
            isDryRun,
        });
        const { canceled, dryRunUpdates } = cancelVisitResponse;
        if (isDryRun)
            console.info('cancelVisit: dry run', dryRunUpdates);
        setDraftWaypointCanceledLocally(draftWaypointID, canceled);
        const visitCanceledMessage = `Visit canceled${isDryRun ? ' (dry run)' : ''}`;
        toastSuccess(visitCanceledMessage);
    };
    return {
        $state: readonly(state),
        cancelVisit,
        fetchDenormalizedDraftWaypoints,
        fetchStaffingOffice,
        getSupplyInstructions,
        getters,
        isLoading,
        setDraftWaypointApartmentAndInstructionsLocally,
        setDraftWaypointCanceledLocally,
        setDraftWaypointScheduledLocally,
        setDraftWaypointShipmentTrackingLocally,
        storeDecryptedDenormalizedDraftWaypoints,
    };
});

export { useDraftWaypointsStore };
